import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ChevronDown } from 'lucide-react';
import { Logo } from '../../components/Logo';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const faqData: FAQSection[] = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'What is Aligned QED?',
        answer:
          'Aligned QED is a web-based LaTeX editor with real-time PDF preview, AI assistance, formal verification, and real-time collaboration. There is nothing to install\u2014just open your browser and start writing. It is designed for researchers, students, and technical writers who want a modern, streamlined LaTeX workflow.',
      },
      {
        question: 'Do I need to install anything?',
        answer:
          'No. Aligned QED runs entirely in your browser. There is no software to download, install, or configure. Just sign in and start editing.',
      },
      {
        question: 'What file types are supported?',
        answer:
          'Aligned QED supports .tex, .bib, .py, and .lean files, as well as image uploads and folders for organizing your project. The editor provides syntax highlighting for LaTeX, BibTeX, and other supported file types.',
      },
      {
        question: 'How do I create my first project?',
        answer:
          'Sign in to your account and click "New Project" on the projects dashboard. You can choose from several templates: a blank LaTeX project, an Open Problem with a Python verifier, or an Open Problem with a Lean verifier (for mathematics). A main.tex file is created automatically so you can start writing right away.',
      },
    ],
  },
  {
    title: 'Writing & Editing',
    items: [
      {
        question: 'What editor features are available?',
        answer:
          'The editor includes syntax highlighting, bracket matching, line numbers, word wrap, spellcheck, and save with Cmd/Ctrl+S. It is built on Monaco\u2014the same engine that powers VS Code\u2014so you get a familiar, powerful editing experience. A LaTeX toolbar provides quick insertion of common commands, math environments, and document structures.',
      },
      {
        question: 'Is there a spellchecker?',
        answer:
          'Yes. Toggle spellcheck using the button in the compile bar. It is LaTeX-aware, so it skips commands, math mode, comments, and brace arguments\u2014only checking the actual prose in your document. Right-click an underlined word for spelling suggestions.',
      },
      {
        question: 'Can I organize files into folders?',
        answer:
          'Yes. The file tree sidebar supports creating folders, renaming files, and deleting files so you can organize your project however you like.',
      },
      {
        question: 'Can I upload images and other files?',
        answer:
          'Yes. You can upload images and other files directly into your project. Large files are automatically handled via cloud storage so your editor stays fast.',
      },
    ],
  },
  {
    title: 'Compiling & Preview',
    items: [
      {
        question: 'How do I compile my document?',
        answer:
          'Click the Compile button in the editor toolbar or use the keyboard shortcut. The compiled PDF will appear in the preview panel on the right. The first compilation may take a moment while packages are loaded.',
      },
      {
        question: 'What LaTeX packages are supported?',
        answer:
          'A wide range of commonly used packages are supported, including amsmath, graphicx, hyperref, biblatex, geometry, and many more.',
      },
      {
        question: 'Can I download the compiled PDF?',
        answer:
          'Yes. Use the download button in the PDF preview panel to save the compiled PDF to your computer.',
      },
      {
        question: 'Can I download my entire project?',
        answer:
          'Yes. You can download your entire project as a ZIP archive, including all files and folders. This is useful for local backups or for working with your project in another editor.',
      },
    ],
  },
  {
    title: 'AI Assistance',
    items: [
      {
        question: 'How does AI editing work?',
        answer:
          'Use the prompt bar at the bottom of the editor to ask the AI to make changes to your document. You can ask it to write equations, fix compilation errors, restructure sections, or anything else. Suggested changes are shown as reviewable diffs that you can accept or reject individually.',
      },
      {
        question: 'What is AI Chat?',
        answer:
          'AI Chat is a conversational assistant you can open alongside your editor. Unlike AI editing (which modifies your document), Chat lets you ask questions, brainstorm ideas, get explanations of LaTeX concepts, or discuss your project without making changes to any files. Chat history is saved per project so you can pick up where you left off.',
      },
      {
        question: 'Does the AI understand my whole project?',
        answer:
          'Yes. The AI automatically includes context from referenced files (via \\input{}, \\include{}, and \\bibliography{} commands) when making suggestions. This means it understands the structure and content of your full document, not just the file you are currently editing.',
      },
      {
        question: 'What happens if an AI edit breaks my document?',
        answer:
          'If an accepted AI edit causes a compilation error, the system will automatically attempt to repair it. This compile-repair cycle can run up to two times to resolve the issue without any manual intervention.',
      },
      {
        question: 'Can I use AI on a specific section?',
        answer:
          'Yes. Select the text you want help with, then press Cmd/Ctrl+Shift+K to open a selection-based prompt targeting just that portion of your document.',
      },
      {
        question: 'Is my work used to train AI models?',
        answer:
          'No. AI assistance runs on a dedicated, independent LLM. Your documents are never used for model training.',
      },
    ],
  },
  {
    title: 'Verifiers',
    items: [
      {
        question: 'What are verifiers?',
        answer:
          'Verifiers let you run code that checks or validates your work directly from the editor. Aligned QED supports two types: a Python verifier for running Python scripts and a Lean verifier for formal mathematical proof verification. Verifiers are especially useful for Open Problem projects where you want to test solutions automatically.',
      },
      {
        question: 'How do I use the Python verifier?',
        answer:
          'Create or open a project that includes a Python verifier file (verifier.py). Click the "Run Verifier" button to execute the script. The verifier output\u2014including pass/fail status and any printed output\u2014is displayed in real time in the editor.',
      },
      {
        question: 'How do I use the Lean verifier?',
        answer:
          'The Lean verifier is available for mathematics projects. Add .lean files to your project and run the verifier to formally check your proofs. Like the Python verifier, results are streamed in real time.',
      },
      {
        question: 'Can the AI generate input for verifiers?',
        answer:
          'Yes. For projects with a prompt.tex file, the AI can generate candidate answers that are then passed to the verifier automatically. This enables a workflow where the AI proposes solutions and the verifier checks their correctness.',
      },
      {
        question: 'Are there usage limits for verifiers?',
        answer:
          'Yes. Verifiers are rate-limited to ensure fair usage: up to 5 runs per minute and 1,000 runs per day per user. Each project can have up to 2 verifier runs in progress at the same time.',
      },
    ],
  },
  {
    title: 'Collaboration & Sharing',
    items: [
      {
        question: 'How do I share a project?',
        answer:
          'Click the "Share" button in the editor header (available to project owners only). This generates a share link with the permission level of your choice.',
      },
      {
        question: 'What permission levels are available?',
        answer:
          'There are three levels: Read (view only), Comment (view and leave comments), and Edit (full editing access).',
      },
      {
        question: 'Can multiple people edit at the same time?',
        answer:
          'Yes. Aligned QED supports real-time collaboration. When multiple users are editing the same project, you can see who is online, which file they are working on, and where their cursor is. Edits from all collaborators are merged automatically so no one overwrites anyone else\u2019s work.',
      },
      {
        question: 'Can collaborators leave comments?',
        answer:
          'Yes. Users with Comment or Edit access can select text and leave comments. Comments are anchored to specific lines in the document, support threaded replies, and can be resolved when addressed.',
      },
    ],
  },
  {
    title: 'Reviews & Submissions',
    items: [
      {
        question: 'What is the review workflow?',
        answer:
          'Project owners can submit their work for review. Once submitted, a reviewer is assigned to evaluate your project. You will receive email notifications when your review status changes\u2014for example, when your project is approved or when the reviewer requests changes.',
      },
      {
        question: 'What review statuses are there?',
        answer:
          'A project moves through several stages: Waiting for Review (just submitted), In Review (a reviewer is evaluating it), Approved (accepted), or Needs Work (the reviewer has requested changes with notes explaining what to address). You can resubmit after making the requested changes.',
      },
    ],
  },
  {
    title: 'Revision History',
    items: [
      {
        question: 'How does revision history work?',
        answer:
          'A revision is automatically saved every time you compile. You can view your revision history by clicking the history icon in the PDF panel. Each revision stores your files and a snapshot of the compiled PDF.',
      },
      {
        question: 'Can I restore a previous version?',
        answer:
          'Yes. Open the revision history, preview a revision\'s code and PDF, and click "Restore This Version" to revert to it. Your current state is always saved on the last compile, so nothing is lost.',
      },
    ],
  },
  {
    title: 'Privacy & Security',
    items: [
      {
        question: 'Where is my data stored?',
        answer:
          'Your data is stored in Firebase, which runs on Google Cloud infrastructure. All data is encrypted both in transit and at rest.',
      },
      {
        question: 'Who can see my projects?',
        answer:
          'Only you can see your projects by default. Others can only access a project if you explicitly share a link with them. You control the permission level for each share link and can revoke access at any time.',
      },
    ],
  },
];

function AccordionItem({ question, answer }: FAQItem) {
  const [isOpen, setIsOpen] = useState(false);
  const id = question.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '');

  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left text-gray-900 font-medium"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={`faq-${id}`}
      >
        <span>{question}</span>
        <ChevronDown
          className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div id={`faq-${id}`} role="region" className="px-5 pb-4 text-gray-600">{answer}</div>
      )}
    </div>
  );
}

export function FAQPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FileText className="h-8 w-8 text-primary" />
            <Logo className="text-xl font-bold text-gray-900" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/faq"
              className="hidden sm:inline text-gray-600 hover:text-gray-900 font-medium"
            >
              FAQ
            </Link>
            <Link
              to="/auth"
              className="hidden sm:inline text-gray-600 hover:text-gray-900 font-medium"
            >
              Sign In
            </Link>
            <Link
              to="/auth"
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-primary text-white text-sm sm:text-base font-medium rounded-lg hover:bg-primary-hover transition-colors"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about using Aligned QED. Can't find
            what you're looking for? Feel free to reach out.
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto space-y-12">
          {faqData.map((section) => (
            <div key={section.title}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {section.title}
              </h2>
              <div className="space-y-3">
                {section.items.map((item) => (
                  <AccordionItem
                    key={item.question}
                    question={item.question}
                    answer={item.answer}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <FileText className="h-6 w-6 text-gray-400" />
            <Logo className="text-white font-medium" />
          </div>
          <div className="flex gap-6 text-sm">
            <a href="/#features" className="hover:text-white transition-colors">
              Features
            </a>
            <Link to="/faq" className="hover:text-white transition-colors">
              FAQ
            </Link>
          </div>
          <div className="text-sm">
            &copy; {new Date().getFullYear()} <Logo />. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
